import { Type } from '@angular/core';
import {
  BaseIconComponent,
  EmptyStateProtectedInformationIconComponent,
  UnderReviewIconComponent,
} from '@ui-lib';
import { AlertActionEnum } from '@common/enums/alert-action.enum';
import { AlertStateEnum } from '@common/enums/alert-state.enum';

/**
 * Copy + visuals for one blocking-status popup.
 *
 * Button rule (derived from `primaryKey`):
 * - `primaryKey` present (actionable states) → two buttons: the action CTA
 *   (routes to the flow) + a "Maybe later" button that just closes the popup.
 * - `primaryKey` absent (in_review / blocked) → a single "Back to home" button
 *   that closes and navigates home.
 */
export interface BlockedPopupCopy {
  titleKey: string;
  descriptionKey: string;
  icon: Type<BaseIconComponent>;
  /** Action CTA translation key. Present only for actionable states. */
  primaryKey?: string;
}

/**
 * Popup copy keyed by the investment flow's `action.key`, used when the flow is
 * ACTION_REQUIRED (e.g. pending bank account / submit KYC). Primary CTA routes
 * to the flow that clears the block.
 */
export const INVESTMENT_ACTION_POPUP: Record<
  AlertActionEnum,
  BlockedPopupCopy
> = {
  [AlertActionEnum.SUBMIT_NAFATH]: {
    titleKey: 'investment_popup_submit_nafath_title',
    descriptionKey: 'investment_popup_submit_nafath_description',
    icon: EmptyStateProtectedInformationIconComponent,
    primaryKey: 'investment_popup_submit_nafath_cta',
  },
  [AlertActionEnum.SUBMIT_KYC]: {
    titleKey: 'investment_popup_submit_kyc_title',
    descriptionKey: 'investment_popup_submit_kyc_description',
    icon: EmptyStateProtectedInformationIconComponent,
    primaryKey: 'investment_popup_submit_kyc_cta',
  },
  [AlertActionEnum.RENEW_KYC]: {
    titleKey: 'investment_popup_renew_kyc_title',
    descriptionKey: 'investment_popup_renew_kyc_description',
    icon: EmptyStateProtectedInformationIconComponent,
    primaryKey: 'investment_popup_renew_kyc_cta',
  },
  [AlertActionEnum.ADD_BANK_ACCOUNT]: {
    titleKey: 'investment_popup_add_bank_account_title',
    descriptionKey: 'investment_popup_add_bank_account_description',
    icon: EmptyStateProtectedInformationIconComponent,
    primaryKey: 'investment_popup_add_bank_account_cta',
  },
  [AlertActionEnum.SUBMIT_WAKALA]: {
    titleKey: 'investment_popup_submit_wakala_title',
    descriptionKey: 'investment_popup_submit_wakala_description',
    icon: EmptyStateProtectedInformationIconComponent,
    primaryKey: 'investment_popup_submit_wakala_cta',
  },
};

/**
 * Popup copy keyed by flow state, used when there is no actionable `action`
 * (e.g. IN_REVIEW = pending review, BLOCKED = rejected). Neither has a primary
 * action, so both render only the single "Back to home" button.
 */
export const INVESTMENT_STATE_POPUP: Partial<
  Record<AlertStateEnum, BlockedPopupCopy>
> = {
  [AlertStateEnum.IN_REVIEW]: {
    titleKey: 'investment_popup_in_review_title',
    descriptionKey: 'investment_popup_in_review_description',
    icon: UnderReviewIconComponent,
  },
  [AlertStateEnum.BLOCKED]: {
    titleKey: 'investment_popup_blocked_title',
    descriptionKey: 'investment_popup_blocked_description',
    icon: UnderReviewIconComponent,
  },
};

/**
 * Popup copy for the minor-proxy gate (investor is a minor and needs a
 * guardian/proxy wakala submitted/reviewed). Takes priority over the generic
 * `submit_wakala` action copy — same action.key, but a distinct reason
 * (`reason.gate === 'minor_proxy'`). Two sub-states via `reason.status`,
 * mirroring the old app's `ProxyPendingSubmissionStrategy` /
 * `ProxyPendingReviewStrategy` split (copy carried over, key names follow
 * this app's `investment_popup_*` convention instead of the old naming).
 */
export const INVESTMENT_MINOR_PROXY_PENDING_SUBMISSION_POPUP: BlockedPopupCopy =
  {
    titleKey: 'investment_popup_minor_proxy_pending_submission_title',
    descriptionKey:
      'investment_popup_minor_proxy_pending_submission_description',
    icon: EmptyStateProtectedInformationIconComponent,
    primaryKey: 'investment_popup_minor_proxy_pending_submission_cta',
  };

/** Already submitted, awaiting review — no action left to take, so just "Back to home". */
export const INVESTMENT_MINOR_PROXY_PENDING_REVIEW_POPUP: BlockedPopupCopy = {
  titleKey: 'investment_popup_minor_proxy_pending_review_title',
  descriptionKey: 'investment_popup_minor_proxy_pending_review_description',
  icon: UnderReviewIconComponent,
};

/** Generic fallback for an unmapped ACTION_REQUIRED action.key (future-proof). */
export const INVESTMENT_GENERIC_ACTION_POPUP: BlockedPopupCopy = {
  titleKey: 'investment_popup_generic_action_title',
  descriptionKey: 'investment_popup_generic_action_description',
  icon: EmptyStateProtectedInformationIconComponent,
  primaryKey: 'investment_popup_generic_action_cta',
};

/** Generic fallback for any unmapped non-passed state (future-proof). */
export const INVESTMENT_GENERIC_BLOCKED_POPUP: BlockedPopupCopy = {
  titleKey: 'investment_popup_generic_blocked_title',
  descriptionKey: 'investment_popup_generic_blocked_description',
  icon: UnderReviewIconComponent,
};
